import { LoginSchema } from '@serviquim/contracts';
import { type FormEvent, useState } from 'react';
import { AppMark } from '../components/AppMark';
import { Button } from '../components/ui/Button';
import { TextField } from '../components/ui/TextField';
import { useT } from '../i18n';
import { login } from '../lib/auth';
import { ApiError, NetworkError } from '../lib/http';

export function LoginPage() {
  const { t } = useT();
  const [tenantSlug, setTenantSlug] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const parsed = LoginSchema.safeParse({ tenantSlug, email, senha });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t('auth.errors.fillAll'));
      return;
    }

    setSubmitting(true);
    setError(undefined);
    try {
      await login(parsed.data.tenantSlug, parsed.data.email, parsed.data.senha);
    } catch (err) {
      if (err instanceof NetworkError) {
        setError(t('auth.errors.offline'));
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(t('auth.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-8 px-6 py-10">
      <div className="flex flex-col items-center gap-3">
        <AppMark size={48} />
        <div className="text-center">
          <h1 className="font-display text-xl font-semibold text-ink">{t('app.name')}</h1>
          <p className="text-sm text-ink-muted">{t('app.tagline')}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex w-full max-w-sm flex-col gap-4">
        <TextField
          label={t('auth.company')}
          name="tenantSlug"
          autoComplete="organization"
          autoCapitalize="none"
          placeholder={t('auth.companyPlaceholder')}
          value={tenantSlug}
          onChange={(event) => setTenantSlug(event.target.value)}
          required
        />
        <TextField
          label={t('auth.email')}
          name="email"
          type="email"
          autoComplete="username"
          autoCapitalize="none"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
        <TextField
          label={t('auth.password')}
          name="senha"
          type="password"
          autoComplete="current-password"
          value={senha}
          onChange={(event) => setSenha(event.target.value)}
          required
        />

        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}

        <Button type="submit" fullWidth loading={submitting}>
          {t('auth.submit')}
        </Button>
      </form>
    </div>
  );
}
